'use client'

import { useState } from 'react'
import { motion } from 'framer-motion'
import { ArrowRight, Eye, EyeOff, Home, Loader2, XCircle } from 'lucide-react'
import { useVerifyResetFlow } from '@/lib/commerce/hooks'

const inputStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.035)',
  border: '1px solid rgba(255,255,255,0.07)',
  color: 'white',
}

const labelClass = 'block text-[11px] font-semibold uppercase tracking-[0.18em] mb-2'
const labelStyle: React.CSSProperties = { color: 'rgba(255,255,255,0.7)' }

export default function VerifyReset({ vendor }: { vendor: any }) {
  const f = useVerifyResetFlow(vendor)
  const [showPassword, setShowPassword] = useState(false)
  const [showPasswordRep, setShowPasswordRep] = useState(false)

  if (f.loading) {
    return (
      <div className="w-full flex items-center justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin" style={{ color: 'rgba(255,255,255,0.6)' }} />
      </div>
    )
  }

  if (!f.valid) {
    return (
      <div className="w-full flex flex-col items-center text-center">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.1, duration: 0.3 }}>
          <div
            className="w-16 h-16 rounded-full inline-flex items-center justify-center"
            style={{ background: 'rgba(239,68,68,0.14)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5' }}
          >
            <XCircle className="h-8 w-8" />
          </div>
        </motion.div>
        <motion.h1
          initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="mt-6 font-semibold tracking-[-0.025em] leading-[1.05] text-white"
          style={{ fontFamily: 'Gilroy, Inter, sans-serif', fontSize: 'clamp(24px, 3vw, 32px)' }}
        >
          Код хүчингүй болсон байна
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
          className="mt-3 text-[14px] leading-6 max-w-sm"
          style={{ color: 'rgba(255,255,255,0.6)' }}
        >
          Сэргээх холбоосын хугацаа дууссан эсвэл хэрэглэгдсэн байна. Шинээр хүсэлт илгээж дахин оролдоно уу.
        </motion.p>
        <motion.button
          initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
          onClick={f.goToLogin}
          className="mt-7 h-12 px-6 rounded-full text-[14px] font-medium inline-flex items-center justify-center gap-2"
          style={{ background: 'white', color: '#0a0a0c' }}
        >
          <Home className="h-4 w-4" /> Нэвтрэх рүү буцах
        </motion.button>
      </div>
    )
  }

  return (
    <div className="w-full">
      <div className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: '#a8a8b3' }}>Шинэ нууц үг</div>
      <h1
        className="mt-3 font-semibold tracking-[-0.025em] leading-[0.95] text-white"
        style={{ fontFamily: 'Gilroy, Inter, sans-serif', fontSize: 'clamp(30px, 4vw, 38px)' }}
      >
        Тогтоох.
      </h1>

      <form onSubmit={f.handleResetPassword} className="mt-8 space-y-4">
        <div>
          <label className={labelClass} style={labelStyle}>Шинэ нууц үг</label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={f.password}
              placeholder="Хамгийн багадаа 8 тэмдэгт"
              onChange={(e) => f.setPassword(e.target.value)}
              onKeyDown={f.handleKeyDown}
              className="w-full h-12 rounded-full pl-5 pr-12 text-[13.5px] outline-none focus:ring-0 placeholder:text-white/30"
              style={inputStyle}
            />
            <button type="button" onClick={() => setShowPassword(s => !s)} aria-label="Toggle" className="absolute right-4 top-1/2 -translate-y-1/2 hover:text-white" style={{ color: 'rgba(255,255,255,0.5)' }}>
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <div className="mt-2 text-[11px]" style={{ color: 'rgba(255,255,255,0.5)' }}>
            8-аас дээш тэмдэгтэй байх ёстой
          </div>
        </div>
        <div>
          <label className={labelClass} style={labelStyle}>Шинэ нууц үг давтах</label>
          <div className="relative">
            <input
              type={showPasswordRep ? 'text' : 'password'}
              value={f.passwordRep}
              placeholder="Шинэ нууц үг давтах"
              onChange={(e) => f.setPasswordRep(e.target.value)}
              onKeyDown={f.handleKeyDown}
              className="w-full h-12 rounded-full pl-5 pr-12 text-[13.5px] outline-none focus:ring-0 placeholder:text-white/30"
              style={inputStyle}
            />
            <button type="button" onClick={() => setShowPasswordRep(s => !s)} aria-label="Toggle" className="absolute right-4 top-1/2 -translate-y-1/2 hover:text-white" style={{ color: 'rgba(255,255,255,0.5)' }}>
              {showPasswordRep ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
        <button
          type="submit"
          disabled={f.btnLoading}
          className="w-full h-12 rounded-full text-[14px] font-medium inline-flex items-center justify-center gap-2 transition-opacity disabled:opacity-60"
          style={{ background: 'white', color: '#0a0a0c' }}
        >
          {f.btnLoading ? <><Loader2 className="h-4 w-4 animate-spin" /> Шинэчилж байна...</> : <>Нууц үг солих <ArrowRight className="h-3.5 w-3.5" /></>}
        </button>
      </form>
    </div>
  )
}
